package com.crowpay.views.components.project.work.scope

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.completionResponse.CompletionFlowPermissions
import com.crowpay.completionResponse.CompletionResponse
import com.crowpay.extensions.handleOne
import com.crowpay.extensions.isUnread
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.lensUnread
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.views.components.Indent
import com.crowpay.views.components.actionIcon
import com.crowpay.views.components.expandAndMarkAsReadAction
import com.crowpay.views.components.expandIcon
import com.crowpay.views.components.indentedCol
import com.crowpay.views.components.infoBubble
import com.crowpay.views.components.nav.navigatorBasedOnPageWidth
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.priceWithChange
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.components.unreadIconVisible
import com.crowpay.views.dialogs.*
import com.crowpay.views.screens.common.ActionImportance
import com.crowpay.views.screens.common.HasProjectData
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.lens
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.reactive.waitForNotNull
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.now
import kotlinx.coroutines.launch


fun ProjectView.renderScopeView(
    scope: Readable<ScopeView>,
    writer: ViewWriter
) = with(writer) {
    if (viewIsNarrow) scopeViewCard(scope, writer)
    else renderScopeViewWide(scope, writer)
}

private fun ProjectView.renderScopeViewWide(
    scope: Readable<ScopeView>,
    writer: ViewWriter
) = with(writer) {
    val expand = Property(false)
    val items = lineItems.shareFilter { it.scopeView == scope()._id }

    val scopeUnread = currentSession.lensUnread { session ->
        session.scopeViews.handleOne { scope() } +
                session.lineItems.isUnread { items().map { it.wraps } } +
                session.contractorNotes.isUnread { contractorNotes().filter { note -> note.lineItem in items().map { it._id } } } +
                session.punchLists.isUnread { punchListItems().filter { note -> note.lineItem in items().map { it._id } } } +
                session.itemChanges.isUnread { items().flatMap { it.changes } } +
                session.lineItems.creditsIsUnread { items().map { it.wraps } }
    }

    col {
        spacing = 0.px
        val weights = WorkItemWeights(viewIsNarrow)

        sizeConstraints(height = 3.rem) - row {
            spacing = 0.px
            launch {
                JumpTo.ScopeView.newTarget(scope()._id, this@row) {
                    expand.value = true
                }
            }

            expanding - ExpandButtonSemantic.onNext - button {
                spacing = 0.px
                dynamicTheme {
                    if (expand()) SectionSemantic else null
                }
                row {
                    spacing = AppDimensions.buttonSpacing
                    weight(weights.nameWeight) - compact - row {
                        spacing = AppDimensions.expandButtonSpace
                        sizeConstraints(width = AppDimensions.sectionIndent) - centered - row {
                            spacing = 0.px
                            expanding - space()
                            centered - unreadIconVisible { scopeUnread()() }
                        }
                        centered - expandIcon(expand)
                        centered - actionIcon {
                            val ids = items().map { it._id }
                            val perms = workItemPermissions().getMany(ids).filter { it.awaitingResponse && it.messages.isNotEmpty() }
                            if (perms.isEmpty()) return@actionIcon null

                            if (perms.any { it.disputeWasFiled }) ActionImportance.Critical
                            else ActionImportance.Important
                        }
                        centered - onlyWhen { scope().scopeSet && !expand() } - scopeSetIcon(1.rem)
                        centered - body { ::content { scope().scopeTitle } }
                    }
                    weight(weights.infoWeight) - row {
                        ::exists { !expand() }
                        weight(weights.amountWeight) - centered - stack {
                            centered - priceWithChange(
                                price = { items().sumOf { it.wraps.price } },
                                change = { items().sumOf { it.wraps.price - it.wraps.originalPrice } }
                            )
                        }
                        val paid = shared {
                            val lines = items()
                            val draws = payAppItems()
                            lines.sumOf { line ->
                                if (line.complete != null) line.wraps.price
                                else draws
                                    .filter { it.lineItem == line._id && (it.pending || it.accepted == true) }
                                    .sumOf { it.amount }
                            }
                        }
//                        weight(weights.paidWeight) - centered - body {
//                            align = Align.Center
//                            ::content { paid().renderDollars() }
//                        }
                        weight(weights.percentCompleteWeight) - centered - body {
                            align = Align.Center
                            ::content {
                                (paid().toDouble() / items().sumOf { it.wraps.price }).renderPercent()
                            }
                        }
                        weight(weights.statusWeight) - centered - body {
                            val status = shared {
                                if (scope().scopeSet) items().firstOrNull()?.state
                                else null
                            }
                            dynamicTheme { status()?.let(::LineItemStateSemantic) }
                            align = Align.Center
                            ::content { status()?.displayName ?: "" }
                        }
                    }
                }
                action = expandAndMarkAsReadAction(expand, scopeUnread)
            }
            if (lens == ProjectLens.Contractor) onlyWhen { expand() } - row {
                spacing = 2.dp
                dynamicTheme {
                    if (expand()) SectionSemantic else null
                }
                centered - buttonTheme - button {
                    icon(Icon.edit.resize(1.2.rem), "Edit Scope")
                    onClick {
                        dialogScreenNavigator.navigate(
                            EditScopeViewDialog(
                                options = lineItems(),
                                scope = scope()
                            )
                        )
                    }
                }
                centered - deleteButton - button {
                    icon(Icon.trash.resize(1.2.rem), "Delete Scope")
                    onClick {
                        dialogScreenNavigator.navigate(
                            GenericConfirmationDialog(
                                header = "Delete Scope ${if (scope().scopeSet) "Set" else "View"}?",
                                message = "You are about to delete this scope ${if (scope().scopeSet) "set" else "view"} This action cannot be undone. Are you sure?",
                                confirmationText = "Delete Scope",
                                confirmationTheme = DangerButtonSemantic,
                                onSubmit = {
                                    if (it) {
                                        val session = notNullSession()
                                        session.scopeViews[scope()._id].delete()
                                    }
                                }
                            )
                        )
                    }
                }
            }
        }
        onlyWhen { expand() } - indentedCol(Indent.HalfSection) {
            space(0.2)

            val pendingPayApp = shared {
                val ids = items().map { it._id }
                payAppItems().any { it.lineItem in ids && it.pending }
            }

            val pendingChange = shared {
                val ids = items().map { it._id }
                pendingItemChanges().any { it.itemId in ids && it.pending }
            }

            body {
                ::exists { !scope().description.isNullOrBlank() }
                ::content { "Description: ${scope().description}" }
            }

            val item = shared { items().firstOrNull() }.waitForNotNull
            lineItemBanners(
                item,
                shared {
                    val line = item()._id
                    lineItemCompletionMessages().filter { it.lineItem == line }
                },
                pendingPayApp,
                pendingChange,
                lens,
                scope
            )

            col {
                spacing = 0.px

                workItemTableHeader(showNameHeader = false)

                greySeparator()

                workItemTableSummary(items, scope, this)

                greySeparator()

                col {
                    spacing = 0.px
                    forEachUpdating(items) { item ->
                        val expandItem = Property(false)

                        val notes = contractorNotes.shareFilter { it.lineItem == item()._id }
                        val tasks = punchListItems.shareFilter { it.lineItem == item()._id }
                        val messages = lineItemCompletionMessages.shareFilter { it.lineItem == item()._id }

                        val unread = currentSession.lensUnread { session ->
                            session.lineItems.handleOne { item().wraps } +
                                    session.contractorNotes.isUnread { notes() } +
                                    session.punchLists.isUnread { tasks() } +
                                    session.itemChanges.isUnread { item().changes } +
                                    session.lineItems.creditsIsUnread { setOf(item().wraps) }
                        }

                        col {
                            spacing = 0.px
                            val row = workItemTableRow(expandItem, item, writer = this, unread = unread)

                            launch {
                                JumpTo.LineItem.newTarget(item()._id, view = row) {
                                    expand.value = true
                                    expandItem.value = true
                                }
                            }

                            onlyWhen { expandItem() } - renderLineItemDetails(
                                item,
                                notes,
                                tasks,
                                messages,
                                expandItem,
                                this
                            )
                            greySeparator()
                        }
                    }
                }
            }

            darkSection - scopeSetActionBar(lens, scope, items, pendingPayApp, this)

            space(0.5)
        }
        greySeparator {
            ::exists { !expand() }
        }
    }
}

fun HasProjectData.scopeSetActionBar(
    lens: ProjectLens,
    scope: Readable<ScopeViewInterface>,
    items: Readable<List<AdjustedLineItem>>,
    pendingPayApp: Readable<Boolean>,
    writer: ViewWriter,
) = with(writer) {
    row {
        spacing = AppDimensions.sectionIndent / 2
        val actions = scopeSetActions(this, lens, scope, items)

        ::exists { scope().scopeSet && actions.any { it() != null } }

        expanding - space()

        row {
            for ((idx, action) in actions.withIndex().reversed()) {
                if (idx == 0 && lens == ProjectLens.Contractor) {
                    centered - onlyWhen(false) { pendingPayApp() } - infoBubble("Unavailable — scope is in a draw under review. Void or wait to enable sign-off.")
                }
                button { applyReactiveActionFormatting(action) }
            }
        }
    }
}

// This is a lot of selectively copied code from lineItemDetails.lineItemAction()
fun HasProjectData.scopeSetActions(
    writer: ViewWriter,
    lens: ProjectLens,
    scope: Readable<ScopeViewInterface>,
    items: Readable<List<AdjustedLineItem>>
): List<Readable<ButtonAction?>> = with(writer) {

    val itemMessages = shared {
        val exampleItem = items().firstOrNull() ?: return@shared emptyList()
        lineItemCompletionMessages().filter { it.lineItem == exampleItem._id }
    }

    val primary = shared {
        val scopeSet = scope()

        if (!scopeSet.scopeSet) return@shared null

        val project = project()
        val scopeItems = items()
        val lineIds = scopeItems.map { it._id }
        val lineItem = scopeItems.firstOrNull() ?: return@shared null

        val isCustomer = (lens != ProjectLens.Contractor)
        val isContractor = (lens == ProjectLens.Contractor)
        val notComplete = (lineItem.state < LineItemState.Complete)
        val started = (lineItem.started != null)
        val notOnHold = lineItem.state != LineItemState.OnHold

        val permissions = CompletionFlowPermissions(lineItem.wraps, isCustomer, itemMessages())

        when {
            isContractor &&
                    notComplete &&
                    !started &&
                    project.state in setOf(ProjectState.PendingStart, ProjectState.InProgress) ->
                ButtonAction("Start Work") {
                    fun confirmation(
                        header: String,
                        message: String,
                        confirmationText: String,
                    ) = GenericConfirmationDialog(
                        header = header,
                        message = message,
                        confirmationText = confirmationText,
                        confirmationTheme = PrimaryButtonSemantic,
                        onSubmit = { confirmed ->
                            if (confirmed) {
                                val session = notNullSession()

                                session.lineItems.bulkModify(
                                    MassModification(
                                        condition<LineItem> {
                                            (it.scopeView eq scopeSet._id) and (it.state eq lineItem.state)
                                        },
                                        modification { it.started assign now() }
                                    )
                                )
                            }
                        }
                    )

                    dialogScreenNavigator.navigate(
                        when {
                            project.started == null -> confirmation(
                                "Start Items and Project?",
                                "Starting this work item will start the project and all items in the scope set",
                                "Start Items and Project"
                            )

                            else -> confirmation(
                                "Start Work Items?",
                                "Starting this work item will start all items in the scope set",
                                "Start Work Items"
                            )
                        }
                    )
                }

            isContractor &&
                    notComplete &&
                    project.state == ProjectState.InProgress &&
                    lineItem.state in setOf(LineItemState.InProgress, LineItemState.WorkingOnRemedy) ->
                ButtonAction(
                    "Request Sign Off",
                    enabled = { payAppItems().none { it.lineItem in lineIds && it.pending } }
                ) {
                    if (punchListItems().filter { it.lineItem == lineItem._id }
                            .any { it.required && it.complete == null })
                        dialogScreenNavigator.navigate(
                            GenericDialog(
                                message = "You still have required punch list items pending. You cannot request review until all required punch list items are complete.",
                            )
                        )
                    else {
                        val lines = items()
                        dialogScreenNavigator.navigate(
                            WorkItemRequestSignOffDialog(
                                lines,
                                lineItemCompletionMessages().filter { it.lineItem == lines.firstOrNull()?._id })
                        )
                    }
                }

            isCustomer &&
                    notComplete &&
                    notOnHold &&
                    permissions.mostRecentProposal is CompletionResponse.RequestComplete ->
                ButtonAction("Approve Work") {
                    dialogScreenNavigator navigateTo GenericConfirmationDialog(
                        header = "Approve & Pay Work Items?",
                        message = "Approving work items releases funds to the contractor as payment. This will happen for each item in the scope set.",
                        confirmationText = "Approve & Pay",
                        confirmationTheme = PrimaryButtonSemantic,
                        onSubmit = { confirmed ->
                            if (confirmed) {
                                val session = notNullSession()
                                repeatForScopeSets(lineItem) { item ->
                                    val result = session.nonCached.lineItem.complete(item._id)
                                    session.lineItems.localSignalUpdate(
                                        matching = { it._id == result._id },
                                        modify = { result }
                                    )
                                }
                            }
                        }
                    )
                }


            else -> null
        }

    }

    val anyUnread = shared {
        val lineIds = items().map { it._id }
        (itemMessages() + lineItemCompletionMessageComments().filter { it.lineItem in lineIds }).isUnread(notNullSession.once().userId)
    }

    val secondary = shared {
        val scopeSet = scope()

        if (!scopeSet.scopeSet) return@shared null

        val project = project()
        val lineItems = items()
        val lineItem = lineItems.firstOrNull() ?: return@shared null

        val isClient = (lens != ProjectLens.Contractor)
        val notComplete = (lineItem.state < LineItemState.Complete)
        val notDisputed = (project.state != ProjectState.Disputed)

        when {
            lineItem.complete != null ->
                ButtonAction.withUnread(
                    "View History",
                    theme = SecondaryButtonSemantic,
                    unread = { anyUnread() }
                ) {
                    navigatorBasedOnPageWidth().navigate(
                        LineItemCompletionForm.createDialog(
                            lineItem,
                            project,
                            lens,
                            LineItemCompletionForm.ScopeSetInfo(scopeSet._id, scopeSet.scopeTitle, lineItems.sumOf { it.price })
                        )
                    )
                }

            isClient &&
                    notComplete &&
                    notDisputed &&
                    lineItem.state == LineItemState.RequestingReview ->
                ButtonAction.withUnread(
                    "Review Request",
                    theme = SecondaryButtonSemantic,
                    unread = { anyUnread() }
                ) {
                    navigatorBasedOnPageWidth().navigate(
                        ScopeSetReviewDialog(lineItems, itemMessages.awaitSuspending(), scope.awaitSuspending())
                    )
                }

            !isClient &&
                    notComplete &&
                    notDisputed &&
                    lineItem.state == LineItemState.RequestingReview ->
                ButtonAction.withUnread(
                    "View Request",
                    theme = SecondaryButtonSemantic,
                    unread = { anyUnread() }
                ) {
                    navigatorBasedOnPageWidth().navigate(
                        LineItemCompletionForm.createDialog(
                            lineItem,
                            project,
                            lens,
                            LineItemCompletionForm.ScopeSetInfo(scopeSet._id, scopeSet.scopeTitle, lineItems.sumOf { it.price })
                        )
                    )
                }

            else -> null
        }
    }

//    val tertiary = shared {
//        val scopeSet = scope()
//
//        if (!scopeSet.scopeSet) return@shared null
//
//        val project = project()
//        val lineItems = items()
//        val lineItem = lineItems.firstOrNull() ?: return@shared null
//
//        val isCustomer = (lens != ProjectLens.Contractor)
//        val notComplete = (lineItem.state < LineItemState.Complete)
//        val notDisputed = (project.state != ProjectState.Disputed)
//
//        when {
//            isCustomer &&
//                    notComplete &&
//                    notDisputed &&
//                    lineItem.state == LineItemState.RequestingReview ->
//                ButtonAction("Minor Adjustment", theme = SecondaryButtonSemantic) {
//                    dialogScreenNavigator.navigate(
//                        MinorAdjustmentsDialog(lineItems.map { it.wraps }, name = scopeSet.scopeTitle)
//                    )
//                }
//
//            else -> null
//        }
//    }

    listOf(primary, secondary)
}